import { Component, OnDestroy, OnInit, TemplateRef, ViewChild, inject } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatTableDataSource } from '@angular/material/table';
import { RootState } from '@next/core-lib/app';
import {
  SummaryCardAction,
  SummaryCardComponent,
  NotificationEmptyComponent,
} from '@next/core-lib/components';
import { DialogService } from '@next/core-lib/dialog';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { TableColumnTypes, TableConfig, TableComponent } from '@next/core-lib/table';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { EditMode, FarmResource, FarmResourcesDivision } from 'src/app/cycle/models/cycle.model';
import { Cycle } from 'src/app/cycle/models/flock-registration-service.model';
import {
  deleteFarmResourceWithConfirmationRequest,
  loadFarmResourcesRequest,
  upsertFarmResourceRequest,
} from 'src/app/cycle/store/actions/cycle.actions';
import {
  getCycleResources,
  getCycles,
  getFarmResources,
} from 'src/app/cycle/store/selectors/cycle.selectors';

import * as cycleActions from '../../../store/actions/cycle.actions';
import { ResourceEditComponent } from '../resource-edit/resource-edit.component';

@Component({
  selector: 'app-farm-resources',
  templateUrl: './farm-resources.component.html',
  imports: [
    SummaryCardComponent,
    MatButton,
    MatIcon,
    TableComponent,
    NotificationEmptyComponent,
    TranslatePipe,
  ],
})
export class FarmResourcesComponent implements OnInit, OnDestroy {
  private readonly store = inject<Store<RootState>>(Store);
  private readonly dialogService = inject(DialogService);
  private readonly locale = inject(LocaleService);

  @ViewChild('resourceColumn') resourceColumn: TemplateRef<unknown>;

  private readonly destroyed$ = new Subject<void>();
  farmResources$: Observable<FarmResource[]> = this.store.select(getFarmResources);
  cycleResources$: Observable<FarmResourcesDivision[]> = this.store.select(getCycleResources);
  cycles$: Observable<Cycle[]> = this.store.select(getCycles);

  tableConfigFarmResource: TableConfig = {
    dataSource: new MatTableDataSource<FarmResource>([]),
    columns: [],
  };

  tableConfigCycleResource: TableConfig = {
    dataSource: new MatTableDataSource<FarmResourcesDivision>([]),
    columns: [],
  };

  summaryCardActions: SummaryCardAction[] = [
    {
      id: '',
    },
  ];

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {}

  ngOnInit(): void {
    this.store.dispatch(loadFarmResourcesRequest());
    this.store.dispatch(cycleActions.loadCyclesRequest());

    this.cycleResources$ = combineLatest([
      this.farmResources$,
      this.cycleResources$,
      this.cycles$,
    ]).pipe(
      map(([farmResources, cycleResources, cycles]) => {
        if (
          !farmResources ||
          farmResources.length === 0 ||
          !cycleResources ||
          cycleResources.length === 0 ||
          !cycles ||
          cycles.length === 0
        ) {
          return [];
        }

        return cycleResources.map((cycleRes) => {
          const cycle = cycles.find((c) => c.id === cycleRes.cycleId);

          const resourceYears = Array.from(
            new Set(farmResources.flatMap((farmResource) => farmResource.year)),
          );

          const cycleStartDate = cycle?.dayOldChicks
            ? Math.min(...cycle.dayOldChicks.map((d) => new Date(d.arrivalDate).getTime()))
            : null;

          const cycleEndDate = cycle?.departures
            ? Math.max(...cycle.departures.map((d) => new Date(d.departureDate).getTime()))
            : null;

          return {
            ...cycleRes,
            cycleNumber: cycle?.cycleNumber,
            cycleStartDate: cycleStartDate,
            cycleEndDate: cycleEndDate,
            status: this.getStatus(cycle, resourceYears),
          };
        });
      }),
    );

    this.tableConfigFarmResource = {
      ...this.tableConfigFarmResource,
      pagination: false,
      selectColumn: false,
      actionColumn: false,
      scrolling: true,
      disableCards: true,
      wrapperWidth: '100%',
      activeSort: {
        id: 'year',
        start: 'desc',
        disableClear: true,
      },
      columns: [
        {
          id: 'year',
          type: TableColumnTypes.NUMBER,
          label: this.locale.translate('sustainability.year'),
          disableFormatting: true,
          className: 'text--bold',
        },
        {
          id: 'water',
          type: TableColumnTypes.NUMBER,
          label: this.locale.translate('sustainability.resource.water'),
          disableFormatting: true,
        },
        {
          id: 'electricity',
          type: TableColumnTypes.NUMBER,
          label: this.locale.translate('sustainability.resource.electricity'),
          disableFormatting: true,
        },
        {
          id: 'electricityPhotovoltaic',
          type: TableColumnTypes.NUMBER,
          label: this.locale.translate('sustainability.resource.electricity-photovoltaic'),
          disableFormatting: true,
        },
        {
          id: 'electricityWind',
          type: TableColumnTypes.NUMBER,
          label: this.locale.translate('sustainability.resource.electricity-wind'),
          disableFormatting: true,
        },
        {
          id: 'naturalGas',
          type: TableColumnTypes.NUMBER,
          label: this.locale.translate('sustainability.resource.natural-gas'),
          disableFormatting: true,
        },
        {
          id: 'diesel',
          type: TableColumnTypes.NUMBER,
          label: this.locale.translate('sustainability.resource.diesel'),
          disableFormatting: true,
        },
        {
          id: 'gasoline',
          type: TableColumnTypes.NUMBER,
          label: this.locale.translate('sustainability.resource.gasoline'),
          disableFormatting: true,
        },
        {
          id: 'propaneLpg',
          type: TableColumnTypes.NUMBER,
          label: this.locale.translate('sustainability.resource.propane-lpg'),
          disableFormatting: true,
        },
        {
          id: 'coal',
          type: TableColumnTypes.NUMBER,
          label: this.locale.translate('sustainability.resource.coal'),
          disableFormatting: true,
        },
      ],
      actions: [
        {
          id: 'edit',
          label: this.locale.translate('edit'),
          callback: (row: FarmResource) => this.openResourceSlideIn(row),
          icon: 'edit',
          disabled: () => false,
        },
        {
          id: 'delete',
          label: this.locale.translate('delete'),
          callback: (row: FarmResource) => this.deleteResource(row),
          icon: 'delete',
          disabled: () => false,
        },
      ],
    };

    this.tableConfigCycleResource = {
      ...this.tableConfigCycleResource,
      pagination: false,
      selectColumn: false,
      actionColumn: false,
      scrolling: true,
      disableCards: true,
      wrapperWidth: '100%',
      activeSort: {
        id: 'cycleNumber',
        start: 'desc',
        disableClear: true,
      },
      columns: [
        {
          id: 'cycleNumber',
          type: TableColumnTypes.NUMBER,
          label: this.locale.translate('cycle'),
          disableFormatting: true,
          className: 'text--bold',
        },
        {
          id: 'cycleStartDate',
          type: TableColumnTypes.DATE,
          label: this.locale.translate('sustainability.resource.cycle-start-date'),
        },
        {
          id: 'cycleEndDate',
          type: TableColumnTypes.DATE,
          label: this.locale.translate('sustainability.resource.cycle-end-date'),
        },
        {
          id: 'water',
          type: TableColumnTypes.NUMBER,
          label: this.locale.translate('sustainability.resource.water'),
          digitsInfo: '1.02-02',
        },
        {
          id: 'electricity',
          type: TableColumnTypes.NUMBER,
          label: this.locale.translate('sustainability.resource.electricity'),
          digitsInfo: '1.02-02',
        },
        {
          id: 'electricityPhotovoltaic',
          type: TableColumnTypes.NUMBER,
          label: this.locale.translate('sustainability.resource.electricity-photovoltaic'),
          digitsInfo: '1.02-02',
        },
        {
          id: 'electricityWind',
          type: TableColumnTypes.NUMBER,
          label: this.locale.translate('sustainability.resource.electricity-wind'),
          digitsInfo: '1.02-02',
        },
        {
          id: 'naturalGas',
          type: TableColumnTypes.NUMBER,
          label: this.locale.translate('sustainability.resource.natural-gas'),
          digitsInfo: '1.02-02',
        },
        {
          id: 'diesel',
          type: TableColumnTypes.NUMBER,
          label: this.locale.translate('sustainability.resource.diesel'),
          digitsInfo: '1.02-02',
        },
        {
          id: 'gasoline',
          type: TableColumnTypes.NUMBER,
          label: this.locale.translate('sustainability.resource.gasoline'),
          digitsInfo: '1.02-02',
        },
        {
          id: 'propaneLpg',
          type: TableColumnTypes.NUMBER,
          label: this.locale.translate('sustainability.resource.propane-lpg'),
          digitsInfo: '1.02-02',
        },
        {
          id: 'coal',
          type: TableColumnTypes.NUMBER,
          label: this.locale.translate('sustainability.resource.coal'),
          digitsInfo: '1.02-02',
        },
        {
          id: 'status',
          type: TableColumnTypes.STRING,
          label: this.locale.translate('sustainability.resource.status'),
          disableFormatting: true,
        },
      ],
      actions: [],
    };

    this.farmResources$.pipe(takeUntil(this.destroyed$)).subscribe((farmResources) => {
      this.tableConfigFarmResource = {
        ...this.tableConfigFarmResource,
        dataSource: new MatTableDataSource<FarmResource>(farmResources),
      };
    });

    this.cycleResources$.pipe(takeUntil(this.destroyed$)).subscribe((cycleResources) => {
      this.tableConfigCycleResource = {
        ...this.tableConfigCycleResource,
        dataSource: new MatTableDataSource<FarmResourcesDivision>(cycleResources),
      };
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  openResourceSlideIn(resourceRow: FarmResource = null) {
    const editMode = resourceRow !== null ? EditMode.Edit : EditMode.Add;

    this.dialogService
      .fullscreenDialog(ResourceEditComponent, {
        confirmButton: true,
        confirmButtonText: this.locale.translate(editMode),
        pushHistoryState: false,
        mode: editMode,
        resourceRow,
      })
      .pipe(takeUntil(this.destroyed$))
      .subscribe(({ result }) => {
        if (result != null) {
          this.saveResource(result.resourceRow);
        }
      });
  }

  saveResource(resourceRow: FarmResource) {
    this.store.dispatch(upsertFarmResourceRequest({ request: resourceRow }));
  }

  deleteResource(resourceRow: FarmResource) {
    this.store.dispatch(deleteFarmResourceWithConfirmationRequest({ id: resourceRow.id }));
  }

  getStatus(cycle: Cycle, resourceYears: number[]) {
    const arrivals = cycle?.dayOldChicks?.map((d) => new Date(d.arrivalDate)) || [];
    const departures = cycle?.departures || [];
    const departureDates = departures.map((d) => new Date(d.departureDate));
    const currentDate = new Date();

    if (!arrivals.length) {
      return this.locale.translate('sustainability.resource.statuses.partial');
    }

    const startYear = Math.min(...arrivals.map((d) => d.getFullYear()));
    const cycleEndDate = departureDates.length
      ? new Date(Math.max(...departureDates.map((d) => d.getTime())))
      : null;
    const cycleEndYear = cycleEndDate?.getFullYear() ?? currentDate.getFullYear();

    const allCycleYears: number[] = [];
    for (let year = startYear; year <= cycleEndYear; year++) {
      allCycleYears.push(year);
    }

    const hasAllResources = allCycleYears.every((year) => resourceYears.includes(year));
    const hasAtLeastOneFullDeparture = departures.some((d) => d.type?.toLowerCase() === 'full');

    if (
      hasAllResources &&
      cycleEndYear !== null &&
      cycleEndDate &&
      cycleEndDate <= currentDate &&
      hasAtLeastOneFullDeparture
    ) {
      return this.locale.translate('sustainability.resource.statuses.complete');
    }

    return this.locale.translate('sustainability.resource.statuses.partial');
  }
}
